import { IHierarchyDefinition, ECheckResultForRemovedPunchData, IUIUser, IDateRange, IDate, IPunchShortDocument, IPunchBundle, IPunchDateData, IPunchSetDocument, IRecord } from "../models"
import AccountStore from "../stores/AccountStore"
import AppStateStore from "../stores/AppStateStore"
import HierarchySettingsStore from "../stores/HierarchySettingsStore"
import strings from "../localization/strings"
import * as api from "../api"
import * as hierarchyHelper from "./hierarchyHelper"
import * as userHelper from "./userHelper"
import * as timeHelper from "./timeHelper"
import * as utils from "../helpers/utils"
import moment from "moment"
import _ from "lodash"

export function getDisablePuncheMessageByDefinition(punchHierarchyId: string, jobId: string, phaseId: string): string {
    if (jobId || phaseId)
        return null
    if (punchHierarchyId) {
        const hierarchyDefinition: IHierarchyDefinition = hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(punchHierarchyId)
        return hierarchyDefinition.EnablePunches ?
            null :
            hierarchyDefinition.MessageForDisablePunches
    }
    else {
        return AccountStore.getCurrentAccountSettings().AllowPunch ?
            null :
            AccountStore.getCurrentAccountSettings().MessageForDisablePunches
    }
}

// check removed hierarchy and removed position for punch
// if removed data exist, show alert, else resolve()
// if employee default position removed by connector, let supervisor know it.
export function checkRemovedDataForPunch(userId: string, hierarchyId: string, shiftId: string, jobId: string, phaseId: string, positionId: string, allowPunchForDeletedPositionId: boolean = false, isEmpDefaultPosition: boolean = false): Promise<ECheckResultForRemovedPunchData> {
    return new Promise(resolve =>
        hierarchyId && !AccountStore.getCurrentAccount().HierarchyItems.find(item => item.Id === hierarchyId) ?
            AppStateStore.setProgressSnackbarContent(strings.Cannot_add_missing_punch_for_removed_hierarchy, () => resolve(ECheckResultForRemovedPunchData.HierarchyRemoved)) :
            positionId ?
                api.getPositions(userId, hierarchyId, shiftId, jobId, phaseId).then(res =>
                    positionId && !res.data.find(item => item.Id === positionId) ?
                        allowPunchForDeletedPositionId ?
                            AppStateStore.setConfirmDialogContent({
                                content: isEmpDefaultPosition ? strings.Employee_default_position_is_not_valid_Are_you_want_punch : strings.The_position_has_been_removed_Are_you_want_punch,
                                confirm: () => resolve(ECheckResultForRemovedPunchData.Valid)
                            }) :
                            AppStateStore.setProgressSnackbarContent(isEmpDefaultPosition ? strings.Employee_default_poisition_is_not_valid : strings.Cannot_add_missing_punch_for_removed_position,
                                () => resolve(ECheckResultForRemovedPunchData.PositionRemoved)
                            ) :
                        resolve(ECheckResultForRemovedPunchData.Valid)
                ) :
                resolve(ECheckResultForRemovedPunchData.Valid)
    )
}

// this function will be used for Global Add/Edit punch
export function getDefaultGlobalPunchHierarchy(employees: IUIUser[]): string {
    let defaultHierarchyId = HierarchySettingsStore.getGlobalLevelItemFilterId() ? HierarchySettingsStore.getGlobalLevelItemFilterId() : null
    const globalPunchEmployeesAccessHierarchyIds = userHelper.getGlobalPunchEmployeesAccessHierarchyIds(employees)
    if (globalPunchEmployeesAccessHierarchyIds.length > 0 && !globalPunchEmployeesAccessHierarchyIds.includes(defaultHierarchyId)) {
        defaultHierarchyId =
            globalPunchEmployeesAccessHierarchyIds.includes(employees[0].DefaultPunchHierarchyId) &&
                employees.filter(i => i.DefaultPunchHierarchyId === employees[0].DefaultPunchHierarchyId).length === employees.length ?
                employees[0].DefaultPunchHierarchyId :
                globalPunchEmployeesAccessHierarchyIds[0]
    }
    return defaultHierarchyId
}

export function getPunchLocked(punchSetDayGroupingTimeLocal: IDate, lockedPeriods: IDateRange[]) {
    const dayGroupingDay = timeHelper.keepConvertUTCToLocal(punchSetDayGroupingTimeLocal)
    const locked = lockedPeriods.find(i => {
        const startDate = timeHelper.keepConvertUTCToLocal(i.StartDate)
        const endDate = timeHelper.keepConvertUTCToLocal(i.EndDate)
        return moment(startDate).isSameOrBefore(dayGroupingDay) && moment(endDate).isSameOrAfter(dayGroupingDay)
    }) !== undefined
    return locked
}

export function getCommonGlobalPunchData(selectedPunchBundles: IPunchBundle[]): IPunchShortDocument {
    const punchDoc: IPunchShortDocument[] = selectedPunchBundles && selectedPunchBundles.map((punchBundle) => {
        const punch = punchBundle.Punch
        return {
            HierarchyItemId: punch.HierarchyItemId || "null",
            JobId: punch.JobId || "null",
            PhaseId: punch.PhaseId || "null",
            PositionId: punch.PositionId || "null",
            PayTypeId: punch.PayTypeId || "null",
            PayClassId: punch.PayClassId || "null",
            EquipmentId: punch.EquipmentId || "null"
        } as IPunchShortDocument
    })

    const commonDiffProps = utils.findCommonDifferentPropertiesInObjects(punchDoc)
    let commonProps = commonDiffProps.common as IPunchShortDocument
    commonProps = Object.keys(commonProps).filter(k => commonProps[k] !== null).reduce((a, k) => ({ ...a, [k]: commonProps[k] }), {})

    Object.keys(commonProps).forEach(k => {
        if (commonProps[k] === "null")
            commonProps[k] = null
    })

    if (!commonProps.hasOwnProperty("HierarchyItemId")) {
        commonProps["HierarchyItemId"] = AccountStore.DiffHierarchyLevel
    }
    if (!commonProps.hasOwnProperty("JobId")) {
        commonProps["JobId"] = AccountStore.DiffJobId
    }
    if (!commonProps.hasOwnProperty("PhaseId")) {
        commonProps["PhaseId"] = AccountStore.DiffPhaseId
    }
    if (!commonProps.hasOwnProperty("PositionId")) {
        commonProps["PositionId"] = AccountStore.DiffPositionId
    }
    if (!commonProps.hasOwnProperty("PayClassId")) {
        commonProps["PayClassId"] = AccountStore.DiffPayClassId
    }
    if (!commonProps.hasOwnProperty("PayTypeId")) {
        commonProps["PayTypeId"] = AccountStore.DiffPayTypeId
    }
    if (!commonProps.hasOwnProperty("EquipmentId")) {
        commonProps["EquipmentId"] = AccountStore.DiffEquipmentId
    }

    if (selectedPunchBundles && selectedPunchBundles.length > 0) {
        commonProps.PunchDateData = selectedPunchBundles.map(i => {
            return {
                UserId: i.Punch.UserId,
                DateTimeLocal: timeHelper.keepConvertUTCToLocal(i.Punch.RoundingRuleId ? i.Punch.RoundedDateTimeLocal : i.Punch.DateTimeLocal)
            } as IPunchDateData
        })
    }
    return commonProps
}

export function checkPhaseFieldAvailable(phase: "singular" | "plural") {
    if (AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels) {
        if (phase === "plural") {
            if (AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.PluralPhaseLabel) {
                return true
            }
        } else if (phase === "singular") {
            if (AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel) {
                return true
            }
            return false
        }

        return false
    }
    return false
}

export function getPunchRecordFromPunchSet(punchId: string, punchSet: IPunchSetDocument) {
    const isPunchIn = punchId === punchSet.PunchInId
    return {
        UserId: punchSet.UserId,
        Punch: {
            Id: punchId,
            UserId: punchSet.UserId,
            AccountId: punchSet.AccountId,
            DateTime: isPunchIn ? punchSet.PunchInTime : punchSet.PunchOutTime,
            DayGroupingTime:  isPunchIn ? punchSet.PunchInTime : punchSet.PunchOutTime,
            DayGroupingTimeLocal: isPunchIn ? punchSet.PunchInTimeLocal : punchSet.PunchOutTimeLocal,
            OverrideDayGroupingTime: punchSet.OverrideDayGroupingTime,
            DateTimeLocal: isPunchIn ? punchSet.PunchInTimeLocal : punchSet.PunchOutTimeLocal,
            RoundedDateTime: isPunchIn ? punchSet.RoundedPunchInTime : punchSet.RoundedPunchOutTime ,
            RoundedDateTimeLocal: isPunchIn ? punchSet.RoundedPunchInTimeLocal : punchSet.RoundedPunchOutTimeLocal, 
            RoundingRuleId: punchSet.RoundingRuleId, 
            HierarchyItemId: punchSet.HierarchyItemId,
            PayrollHierarchyItemId: punchSet.PayrollHierarchyId,
            PayrollPayGroupId: punchSet.PayrollPayGroupId, 
            PositionId: punchSet.PositionId, 
            PositionName: punchSet.PositionName, 
            PayClassId: punchSet.PayClassId,
            EquipmentId: punchSet.EquipmentId, 
            EquipmentName:punchSet.EquipmentName, 
            HierarchyList: punchSet.HierarchyList,
            PayTypeId: punchSet.PayTypeId,
            JobId: punchSet.JobId,
            JobName: punchSet.JobName,
            JobCostCode: punchSet.JobCostCode,
            PhaseId: punchSet.PhaseId,
            PhaseName: punchSet.PhaseName,
            PhaseCostCode: punchSet.PhaseCostCode,
            ShiftId: punchSet.ShiftId,
            BreakId: punchSet.BreakId,
            ScheduleInfo: punchSet.ScheduleInfo,
            TimeOffRequestId: punchSet.TimeOffRequestId,
            HolidayName: punchSet.HolidayName,
            MobileBreakId: punchSet.MobileBreakId,
            MobileBreakName: punchSet.MobileBreakName,
            Calculated: punchSet.Calculated,
        }
    } as IRecord
}